import React, { useEffect, useContext, createContext } from "react";
import { useAppContextQuery } from "Shared/util/web-api-hooks";
import { AppData, BrandName, EIdProvider } from "Shared/util/types";
import { getCookie, addCookie, createCookieMaxAge } from "Shared/util/cookies";

const CULTURE_CODE_COOKIE = "App.CultureCode";
const CURRENCY_CODE_COOKIE = "App.CurrencyCode";

const updateCookies = (cultureCode: string, currencyCode: string) => {
  const cultureCodeCookie = getCookie(CULTURE_CODE_COOKIE);
  if (!cultureCodeCookie) {
    addCookie(CULTURE_CODE_COOKIE, cultureCode, createCookieMaxAge(365));
  }

  const currencyCodeCookie = getCookie(CURRENCY_CODE_COOKIE);
  if (!currencyCodeCookie) {
    addCookie(CURRENCY_CODE_COOKIE, currencyCode, createCookieMaxAge(365));
  }
};

type AppContextModel = {
  appData: AppData;
  loadingAppData: boolean;
};

const defaultAppData: AppData = {
  cultureCode: "",
  currencyCode: "",
  tokenRefreshIntervalMinutes: 30,
  brandName: BrandName.EuroFinans,
  eIdProvider: EIdProvider.SeBankId,
  maintenance: {
    loginDisabled: false,
    loginMessage: "",
  },
  openBankingConfiguration: {
    consentExpiresVerySoonDays: 30,
    consentExpiresSoonDays: 10,
  },
};

const AppContext = createContext<AppContextModel>({
  loadingAppData: true,
  appData: defaultAppData,
});

const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined)
    throw new Error("useAppContext must be used within a AppProvider");
  return context;
};

export const AppProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [data, loading] = useAppContextQuery();

  useEffect(() => {
    if (data && !loading) {
      updateCookies(data.cultureCode, data.currencyCode);
    }
  }, [data, loading]);

  const contextValues = {
    appData: data || defaultAppData,
    loadingAppData: loading,
  };

  return (
    <AppContext.Provider value={contextValues}>{children}</AppContext.Provider>
  );
};

export default useAppContext;
